import { HorizontalRailPointerDrag, HorizontalRailScroller } from "./HorizontalRailDrag";

type CarouselOptions = {
    root: HTMLElement;
    rail: HTMLElement;
    slideSelector: string;
    onSelect: (index: number) => void;
    snapToNearest?: () => boolean;
};

export class HorizontalRailCarousel {
    private originals: HTMLElement[] = [];
    private slides: HTMLElement[] = [];
    private prefix = 0;
    private physicalIndex = 0;
    private gestureIndex = 0;
    private touching = false;
    private dragging = false;
    private settling = false;
    private touchStart?: { x: number; y: number };
    private settleTimer?: ReturnType<typeof setTimeout>;
    private scroller?: HorizontalRailScroller;

    constructor(private readonly options: CarouselOptions) {}

    connect(index: number): void {
        const { root, rail, slideSelector } = this.options;
        rail.dataset.horizontalRailCarousel = "";
        rail.querySelectorAll("[data-horizontal-rail-copy]").forEach(copy => copy.remove());
        this.originals = Array.from(rail.querySelectorAll<HTMLElement>(slideSelector));
        this.reflow(index);
        this.scroller = new HorizontalRailScroller({
            root, railSelector: "[data-horizontal-rail-carousel]", restoreScrollPosition: false,
            onDragStart: this.onDragStart, onDragEnd: this.onDragEnd
        });
        this.scroller.connect();
        rail.addEventListener("scroll", this.onScroll, { passive: true });
        rail.addEventListener("scrollend", this.settle);
        rail.addEventListener("touchstart", this.onTouchStart, { passive: true });
        rail.addEventListener("touchend", this.onTouchEnd, { passive: true });
        rail.addEventListener("touchcancel", this.onTouchCancel, { passive: true });
    }

    disconnect(): void {
        const { rail } = this.options;
        clearTimeout(this.settleTimer);
        this.scroller?.disconnect();
        rail.removeEventListener("scroll", this.onScroll);
        rail.removeEventListener("scrollend", this.settle);
        rail.removeEventListener("touchstart", this.onTouchStart);
        rail.removeEventListener("touchend", this.onTouchEnd);
        rail.removeEventListener("touchcancel", this.onTouchCancel);
        rail.querySelectorAll("[data-horizontal-rail-copy]").forEach(copy => copy.remove());
        rail.style.removeProperty("scroll-snap-type");
        delete rail.dataset.horizontalRailCarousel;
    }

    reflow(index: number): void {
        const { rail } = this.options;
        this.settling = false;
        clearTimeout(this.settleTimer);
        rail.querySelectorAll("[data-horizontal-rail-copy]").forEach(copy => copy.remove());
        const count = this.originals.length;
        this.prefix = count > 1 ? Math.max(2, Math.ceil(rail.clientWidth / (this.originals[0].offsetWidth || rail.clientWidth)) + 1) : 0;
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        for (let i = 0; i < this.prefix; i++) {
            before.append(this.copy(this.mod(i - this.prefix)));
            after.append(this.copy(i % count));
        }
        rail.prepend(before);
        rail.append(after);
        this.slides = Array.from(rail.querySelectorAll<HTMLElement>(this.options.slideSelector));
        this.physicalIndex = this.prefix + Math.max(0, Math.min(index, count - 1));
        rail.scrollLeft = this.position(this.physicalIndex);
        if (count) this.options.onSelect(this.physicalIndex - this.prefix);
    }

    step(direction: number): void {
        if (this.originals.length < 2) return;
        this.goTo(this.physicalIndex + Math.sign(direction));
    }

    private copy(index: number): HTMLElement {
        const copy = this.originals[index].cloneNode(true) as HTMLElement;
        copy.dataset.horizontalRailCopy = "";
        copy.setAttribute("aria-hidden", "true");
        copy.querySelectorAll("img").forEach(image => { image.alt = ""; });
        return copy;
    }

    private position(index: number): number {
        const slide = this.slides[index];
        return slide ? slide.offsetLeft - (this.options.rail.clientWidth - slide.offsetWidth) / 2 : 0;
    }

    private nearest(): number {
        const left = this.options.rail.scrollLeft;
        return this.slides.reduce((best, _, index) =>
            Math.abs(this.position(index) - left) < Math.abs(this.position(best) - left) ? index : best, 0);
    }

    private gestureTarget(direction: number): number {
        const nearest = this.nearest();
        // Short gestures advance once; longer wide-layout drags keep the nearest photo.
        return this.options.snapToNearest?.() && nearest !== this.gestureIndex
            ? nearest
            : this.gestureIndex + direction;
    }

    private mod(index: number): number {
        return (index % this.originals.length + this.originals.length) % this.originals.length;
    }

    private goTo(index: number): void {
        if (!this.slides.length) return;
        const { rail } = this.options;
        rail.style.scrollSnapType = "none";
        if (index < 0 || index >= this.slides.length) {
            // Rebase an unfinished transition when repeated inputs reach the end of the copies.
            const shift = index < 0 ? this.originals.length : -this.originals.length;
            rail.scrollLeft += this.position(this.physicalIndex + shift) - this.position(this.physicalIndex);
            index += shift;
        }
        this.physicalIndex = index;
        this.settling = true;
        this.options.onSelect(this.mod(this.physicalIndex - this.prefix));
        rail.scrollTo({ left: this.position(this.physicalIndex), behavior: "smooth" });
        this.scheduleSettle();
    }

    private readonly onDragStart = (drag: HorizontalRailPointerDrag): void => {
        this.dragging = true;
        this.settling = false;
        this.gestureIndex = this.nearest();
        this.options.rail.style.scrollSnapType = "none";
        drag.rail.setPointerCapture?.(drag.pointerId);
    };

    private readonly onDragEnd = (drag: HorizontalRailPointerDrag): void => {
        this.dragging = false;
        const distance = this.options.rail.scrollLeft - drag.startScrollLeft;
        const direction = drag.hasDragged && Math.abs(distance) >= 12 ? Math.sign(distance) : 0;
        this.goTo(this.gestureTarget(direction));
    };

    private readonly onTouchStart = (event: TouchEvent): void => {
        const touch = event.touches[0];
        if (!touch) return;
        this.touching = true;
        this.settling = false;
        this.touchStart = { x: touch.clientX, y: touch.clientY };
        this.gestureIndex = this.nearest();
        this.options.rail.style.scrollSnapType = "none";
    };

    private readonly onTouchEnd = (event: TouchEvent): void => {
        if (event.touches.length) return;
        this.touching = false;
        const touch = event.changedTouches[0];
        if (touch && this.touchStart) {
            const dx = this.touchStart.x - touch.clientX;
            const dy = this.touchStart.y - touch.clientY;
            const direction = Math.abs(dx) >= 20 && Math.abs(dx) > Math.abs(dy) ? Math.sign(dx) : 0;
            this.goTo(this.gestureTarget(direction));
        }
        this.touchStart = undefined;
        this.scheduleSettle();
    };

    private readonly onTouchCancel = (): void => {
        this.touching = false;
        this.touchStart = undefined;
        this.goTo(this.nearest());
        this.scheduleSettle();
    };

    private readonly onScroll = (): void => { this.scheduleSettle(); };

    private scheduleSettle(): void {
        clearTimeout(this.settleTimer);
        // Fallback for browsers without scrollend. Never reposition during a held gesture.
        this.settleTimer = setTimeout(this.settle, 120);
    }

    private readonly settle = (): void => {
        if (this.touching || this.dragging || !this.slides.length) return;
        const nearest = this.settling ? this.physicalIndex : this.nearest();
        if (Math.abs(this.options.rail.scrollLeft - this.position(nearest)) > 1) {
            this.goTo(nearest);
            return;
        }
        clearTimeout(this.settleTimer);
        this.settling = false;
        const index = this.mod(nearest - this.prefix);
        this.physicalIndex = this.prefix + index;
        // Boundary copies and originals have identical neighbors; normalize only after snapping ends.
        // Finish the last pixel too, so native snapping cannot resume past the chosen photo.
        this.options.rail.scrollLeft = this.position(this.physicalIndex);
        this.options.rail.style.removeProperty("scroll-snap-type");
        this.options.onSelect(index);
    };
}
